/**
 * The wiring portrait, as pure drawing functions.
 *
 * Deliberately DOM free and parameterised by size: the live canvas component
 * and the PNG poster exporter (stage 4) render the same bands at different
 * dimensions by calling `drawPortrait` with a different context and size.
 */

/** Where each band sits horizontally when the organization is hard-wired. */
export const HOME = [0.16, 0.84, 0.3, 0.9, 0.12, 0.7] as const;

/** The stepped BOI gradient the band tones walk through. */
const PAPER = [249, 247, 247] as const;
const MIST = [197, 215, 255] as const;
const BLUE = [10, 10, 229] as const;
const DEEP = [5, 7, 160] as const;

/** Vertical gap between bands, in CSS pixels. */
export const BAND_GAP = 6;
/** Width of one vertical strip, in CSS pixels. */
export const STRIP_WIDTH = 12;

/** The animation state of a single band. */
export interface Band {
  /** Fade-in, 0 (unanswered) to 1 (fully drawn). */
  on: number;
  /** Centre of the Gaussian, 0 to 1 across the width. */
  c: number;
  /** Spread of the Gaussian. */
  sp: number;
  /** The answer score, 0 to 3. Drives how much the band still drifts. */
  score: number;
  /** Phase offset so the bands do not drift in lockstep. */
  phase: number;
}

export interface DrawOptions {
  width: number;
  height: number;
  bands: Band[];
  /** The band under the pointer or keyboard focus, or -1. */
  hoverIndex: number;
  /** Track name per band, used for the empty-band label. */
  tracks: string[];
  /** A monotonic timestamp in milliseconds, for the drift. */
  now: number;
  reducedMotion: boolean;
}

function mix(a: readonly number[], b: readonly number[], t: number): [number, number, number] {
  const at = (index: number) => a[index] ?? 0;
  const bt = (index: number) => b[index] ?? 0;
  return [at(0) + (bt(0) - at(0)) * t, at(1) + (bt(1) - at(1)) * t, at(2) + (bt(2) - at(2)) * t];
}

/**
 * The band tone for an intensity 0 to 1, quantised to 12 steps.
 * The steps are deliberate: they are the BOI stepped-gradient look.
 */
export function tone(intensity: number): string {
  const v = Math.round(Math.max(0, Math.min(1, intensity)) * 12) / 12;
  let rgb: [number, number, number];
  if (v < 0.45) {
    rgb = mix(PAPER, MIST, v / 0.45);
  } else if (v < 0.9) {
    rgb = mix(MIST, BLUE, (v - 0.45) / 0.45);
  } else {
    rgb = mix(BLUE, DEEP, (v - 0.9) / 0.1);
  }
  return `rgb(${Math.trunc(rgb[0])},${Math.trunc(rgb[1])},${Math.trunc(rgb[2])})`;
}

/**
 * Where a band should settle for a given score: as the score rises the centre
 * moves toward 0.55 and the spread grows, so the bands align instead of
 * scattering.
 */
export function targetFor(bandIndex: number, score: number): { c: number; sp: number } {
  const home = HOME[bandIndex] ?? 0.5;
  const ratio = Math.max(0, Math.min(3, score)) / 3;
  return { c: home + (0.55 - home) * ratio, sp: 0.1 + 0.07 * ratio };
}

/** A fresh, unanswered band. */
export function createBand(index: number): Band {
  const home = HOME[index] ?? 0.5;
  return { on: 0, c: home, sp: 0.1, score: 0, phase: index * 1.7 };
}

/** Draw the whole portrait. Clears the context first. */
export function drawPortrait(ctx: CanvasRenderingContext2D, options: DrawOptions): void {
  const { width, height, bands, hoverIndex, tracks, now, reducedMotion } = options;
  const count = bands.length;
  if (count === 0 || width <= 0 || height <= 0) {
    return;
  }

  ctx.clearRect(0, 0, width, height);
  const rowHeight = (height - BAND_GAP * (count - 1)) / count;

  bands.forEach((band, index) => {
    const y = Math.round(index * (rowHeight + BAND_GAP));

    if (band.on < 0.02) {
      ctx.strokeStyle = "rgba(13,19,20,.12)";
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 5]);
      ctx.strokeRect(0.5, y + 0.5, width - 1, rowHeight - 1);
      ctx.setLineDash([]);
      ctx.fillStyle = "rgba(100,106,114,.9)";
      ctx.font = '400 11px "DM Sans", "Helvetica Neue", Arial, sans-serif';
      // The eyebrow's tracking, where the canvas supports it.
      if ("letterSpacing" in ctx) {
        ctx.letterSpacing = "0.66px";
      }
      const label = `0${index + 1}  ${(tracks[index] ?? "").toUpperCase()}`;
      ctx.fillText(label, 10, y + 18);
      if ("letterSpacing" in ctx) {
        ctx.letterSpacing = "0px";
      }
      return;
    }

    const drift = reducedMotion
      ? 0
      : Math.sin(now / 5200 + band.phase) * 0.02 * (1 - Math.max(0, Math.min(3, band.score)) / 3);

    for (let x = 0; x < width; x += STRIP_WIDTH) {
      const d = (x / width - (band.c + drift)) / band.sp;
      ctx.fillStyle = tone(Math.exp(-d * d) * band.on);
      ctx.fillRect(Math.round(x), y, STRIP_WIDTH + 1, Math.round(rowHeight));
    }

    if (hoverIndex === index) {
      ctx.strokeStyle = "#11262B";
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, y + 0.5, width - 1, rowHeight - 1);
    }
  });
}

/** The top and height of a band, for positioning overlays over the canvas. */
export function bandBox(
  index: number,
  count: number,
  height: number,
): { top: number; height: number } {
  const rowHeight = (height - BAND_GAP * (count - 1)) / count;
  return { top: index * (rowHeight + BAND_GAP), height: rowHeight };
}
